import { httpRequest } from "../../http";

export type NetworkKey = "meta" | "snapchat" | "pinterest" | "google" | string;

export interface NetworkViewError {
  code: string
  message: string
}

// ─── Audience Object (nested under audience definition) ──

export interface AudienceObjectInfo {
  audience_id: string | null
  /** Whether this audience is wired into the ad-group/adset/adsquad's targeting as include or exclude. */
  kind: "include" | "exclude"
  network_status: string | null
  approximate_count: number | null
  /** Set when fetching this specific audience object failed; the rest of the view still renders. */
  error: NetworkViewError | null
}

// ─── Network views per level ────────────────────────────

export interface LaunchNetworkView {
  exists: boolean
  network: NetworkKey | null
  launch_id: number | null
  network_id: string | null
  network_url: string | null
  status: string | null
  schedule_status: string | null
  budget: string | null
  raw: Record<string, unknown> | null
  error: NetworkViewError | null
}

export interface AdGroupNetworkView {
  exists: boolean
  network: NetworkKey | null
  ad_group_id: number | null
  network_id: string | null
  network_url: string | null
  status: string | null
  schedule_status: string | null
  /** Daily budget at the adset/adgroup/adsquad level (Meta/Snapchat populate this; Pinterest/Google leave it null). */
  budget: string | null
  /**
   * Audience IDs from our M2M (what we *think* is linked). Compare against
   * `audience_objects[*].audience_id` (what the network actually targets) to
   * surface link drift.
   */
  audience_ids: string[]
  /**
   * One entry per audience reference found in the network's targeting spec
   * (NOT from our M2M). Each entry is tagged `kind: "include" | "exclude"`.
   * Replaces the previous single `audience_object` field.
   */
  audience_objects: AudienceObjectInfo[]
  raw: Record<string, unknown> | null
  error: NetworkViewError | null
}

export interface AdNetworkView {
  exists: boolean
  network: NetworkKey | null
  ad_id: number | null
  network_id: string | null
  network_url: string | null
  status: string | null
  schedule_status: string | null
  headline: string | null
  body_copy: string | null
  link_description: string | null
  link: string | null
  media_url: string | null
  raw: Record<string, unknown> | null
  error: NetworkViewError | null
}

// ─── Tree structure ──────────────────────────────────────

export interface AdView {
  ad_id: number
  ad_name: string
  networks: Record<string, AdNetworkView>
}

export interface AdGroupView {
  ad_group_id: number
  ad_group_name: string
  audience_definition_id: number | null
  audience_name: string | null
  networks: Record<string, AdGroupNetworkView>
  ads: AdView[]
}

export interface LaunchView {
  launch_id: number
  launch_name: string
  networks: Record<string, LaunchNetworkView>
  ad_groups: AdGroupView[]
}

export interface EngagementNetworkViewResponse {
  engagement_id: number
  engagement_name: string
  generated_at: string
  networks: NetworkKey[]
  launches: LaunchView[]
}

// ─── Commands ────────────────────────────────────────────
// Network view is now async via the generic job system:
//   JobService.createJob({ type: "NETWORK_VIEW", engagement_id })

export class CommandGetNetworkView {
  async run(jobId: number): Promise<EngagementNetworkViewResponse> {
    try {
      return await httpRequest<EngagementNetworkViewResponse>(
        `${import.meta.env.VITE_API_PLATFORM_SERVICE_URL}api/v1/content_network/network-view/jobs/${jobId}/`,
        { method: "GET" }
      );
    } catch (error) {
      console.error("Error fetching network view:", error);
      throw error;
    }
  }
}
